// src/features/packages/packagesSlice.js - Avec fetchRecentPackages ajouté
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import packageService from '../../services/packageService';

const initialState = {
  packages: [],
  recentPackages: [],
  currentPackage: null,
  isLoading: false,
  error: null,
  meta: {
    page: 1,
    pages: 1,
    total: 0,
  },
};

// Action asynchrone pour récupérer tous les forfaits
export const fetchPackages = createAsyncThunk(
  'packages/fetchPackages',
  async (params, { rejectWithValue }) => {
    try {
      const data = await packageService.getPackages(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des forfaits'
      );
    }
  }
);

// Action asynchrone pour récupérer les forfaits récents
export const fetchRecentPackages = createAsyncThunk(
  'packages/fetchRecentPackages',
  async (limit = 5, { rejectWithValue }) => {
    try {
      const data = await packageService.getPackages({ 
        limit, 
        page: 1,
        sort: 'createdAt,desc' 
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des forfaits récents'
      );
    }
  }
);

// Action asynchrone pour récupérer un forfait par ID
export const fetchPackageById = createAsyncThunk(
  'packages/fetchPackageById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await packageService.getPackageById(id);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération du forfait'
      );
    }
  }
);

// Action asynchrone pour récupérer les forfaits pour l'administration
export const fetchAdminPackages = createAsyncThunk(
  'packages/fetchAdminPackages',
  async (params, { rejectWithValue }) => {
    try {
      const data = await packageService.getAdminPackages(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la récupération des forfaits'
      );
    }
  }
);

// Action asynchrone pour créer un forfait
export const createPackage = createAsyncThunk(
  'packages/createPackage',
  async (packageData, { rejectWithValue }) => {
    try {
      const data = await packageService.createPackage(packageData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la création du forfait'
      );
    }
  }
);

// Action asynchrone pour mettre à jour un forfait
export const updatePackage = createAsyncThunk(
  'packages/updatePackage',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = await packageService.updatePackage(id, formData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la mise à jour du forfait'
      );
    }
  }
);

// Action asynchrone pour supprimer un forfait
export const deletePackage = createAsyncThunk(
  'packages/deletePackage',
  async (id, { rejectWithValue }) => {
    try {
      await packageService.deletePackage(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Erreur lors de la suppression du forfait'
      );
    }
  }
);

const packagesSlice = createSlice({
  name: 'packages',
  initialState,
  reducers: {
    resetPackageError: (state) => {
      state.error = null;
    },
    resetCurrentPackage: (state) => {
      state.currentPackage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Gestion de fetchPackages
      .addCase(fetchPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages = action.payload.packages;
        state.meta = {
          page: action.payload.page,
          pages: action.payload.pages,
          total: action.payload.total,
        };
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de fetchRecentPackages
      .addCase(fetchRecentPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recentPackages = action.payload.packages;
      })
      .addCase(fetchRecentPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de fetchPackageById
      .addCase(fetchPackageById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPackageById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPackage = action.payload;
      })
      .addCase(fetchPackageById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de fetchAdminPackages
      .addCase(fetchAdminPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages = action.payload.packages;
        state.meta = {
          page: action.payload.page,
          pages: action.payload.pages,
          total: action.payload.total,
        };
      })
      .addCase(fetchAdminPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de createPackage
      .addCase(createPackage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPackage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages.unshift(action.payload);
        state.meta.total += 1;
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de updatePackage
      .addCase(updatePackage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.packages.findIndex(
          (pkg) => pkg._id === action.payload._id
        );
        if (index !== -1) {
          state.packages[index] = action.payload;
        }
        if (state.currentPackage && state.currentPackage._id === action.payload._id) {
          state.currentPackage = action.payload;
        }
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Gestion de deletePackage
      .addCase(deletePackage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages = state.packages.filter(
          (pkg) => pkg._id !== action.payload
        );
        state.meta.total -= 1;
        if (state.currentPackage && state.currentPackage._id === action.payload) {
          state.currentPackage = null;
        }
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetPackageError, resetCurrentPackage } = packagesSlice.actions;
export default packagesSlice.reducer;